import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MethodologyExplainer = () => {
  const [activeTab, setActiveTab] = useState('calculations');

  const calculationMethods = [
    {
      metric: 'Agua Ahorrada',
      icon: 'Droplets',
      color: 'var(--color-primary)',
      formula: 'Kg comida × 15L/kg',
      explanation: `Cada kilogramo de comida rescatada ahorra aproximadamente 15 litros de agua. 
      Este cálculo se basa en el promedio de agua virtual necesaria para producir diferentes tipos de alimentos, 
      considerando riego, procesamiento y limpieza.`,
      sources: ['FAO Water Footprint Assessment', 'UNESCO Water Statistics'],
      example: 'Si rescatas 2kg de comida = 2 × 15 = 30L de agua ahorrada'
    },
    {
      metric: 'CO₂ Evitado',
      icon: 'Leaf',
      color: 'var(--color-success)',
      formula: 'Kg comida × 0.7kg CO₂/kg',
      explanation: `Por cada kilogramo de comida rescatada, evitamos aproximadamente 0.7kg de emisiones de CO₂. 
      Esto incluye las emisiones de producción, transporte, y descomposición en vertederos que se evitan 
      al rescatar el alimento.`,
      sources: ['IPCC Food Waste Report', 'EPA Greenhouse Gas Equivalencies'],
      example: 'Si rescatas 5kg de comida = 5 × 0.7 = 3.5kg CO₂ evitado'
    },
    {
      metric: 'Equivalencias',
      icon: 'TreePine',
      color: 'var(--color-secondary)',
      formula: 'CO₂ evitado ÷ 21kg CO₂/árbol/año',
      explanation: `Para hacer más comprensibles las cifras de CO₂, las convertimos en equivalencias como árboles plantados. 
      Un árbol absorbe aproximadamente 21kg de CO₂ al año, por lo que dividimos el CO₂ evitado entre esta cifra.`,
      sources: ['Forest Service Carbon Calculator', 'Tree Benefit Calculator'],
      example: '10kg CO₂ evitado = 10 ÷ 21 = 0.48 árboles equivalentes'
    }
  ];

  const dataSources = [
    {
      organization: 'FAO (Organización de las Naciones Unidas para la Alimentación)',
      description: 'Datos sobre huella hídrica y desperdicio alimentario global',
      reliability: 'Muy Alta',
      lastUpdate: '2024-01-15'
    },
    {
      organization: 'IPCC (Panel Intergubernamental sobre Cambio Climático)',
      description: 'Factores de emisión para diferentes tipos de alimentos',
      reliability: 'Muy Alta',
      lastUpdate: '2023-12-20'
    },
    {
      organization: 'EPA (Agencia de Protección Ambiental de EE.UU.)',
      description: 'Calculadoras de equivalencias de gases de efecto invernadero',
      reliability: 'Alta',
      lastUpdate: '2024-02-10'
    },
    {
      organization: 'Universidad Politécnica de Madrid',
      description: 'Estudios locales sobre desperdicio alimentario en España',
      reliability: 'Alta',
      lastUpdate: '2024-03-05'
    }
  ];

  const verificationProcess = [
    {
      step: 1,
      title: 'Registro Automático',
      description: 'Cada rescate se registra automáticamente cuando escaneas el código QR',
      icon: 'QrCode'
    },
    {
      step: 2,
      title: 'Validación del Negocio',
      description: 'El negocio confirma el peso y tipo de alimentos en la bolsa sorpresa',
      icon: 'CheckCircle'
    },
    {
      step: 3,
      title: 'Cálculo Automático',
      description: 'Nuestros algoritmos aplican los factores de conversión certificados',
      icon: 'Calculator'
    },
    {
      step: 4,
      title: 'Auditoría Mensual',
      description: 'Revisión independiente de una muestra aleatoria de transacciones',
      icon: 'Shield'
    }
  ];

  const tabs = [
    { id: 'calculations', label: 'Cálculos', icon: 'Calculator' },
    { id: 'sources', label: 'Fuentes', icon: 'BookOpen' },
    { id: 'verification', label: 'Verificación', icon: 'Shield' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
          <Icon name="Info" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg text-foreground">
            Metodología de Cálculo
          </h3>
          <p className="text-sm text-muted-foreground">
            Transparencia total en nuestros cálculos de impacto
          </p>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-6">
        {tabs?.map((tab) => (
          <Button
            key={tab?.id}
            variant={activeTab === tab?.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab?.id)}
            className="flex-1"
          >
            <Icon name={tab?.icon} size={16} className="mr-2" />
            {tab?.label}
          </Button>
        ))}
      </div>
      {/* Calculations Tab */}
      {activeTab === 'calculations' && (
        <div className="space-y-6">
          {calculationMethods?.map((method, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${method?.color}15` }}>
                  <Icon name={method?.icon} size={18} color={method?.color} />
                </div>
                <h4 className="font-medium text-foreground">{method?.metric}</h4>
              </div>

              <div className="space-y-3">
                <div className="bg-background rounded-md p-3 border border-border">
                  <div className="text-sm font-medium text-foreground mb-1">Fórmula:</div>
                  <code className="text-sm text-primary font-mono">{method?.formula}</code>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {method?.explanation}
                </p>

                <div className="bg-success/5 border border-success/20 rounded-md p-3">
                  <div className="text-sm font-medium text-success mb-1">Ejemplo:</div>
                  <p className="text-sm text-foreground">{method?.example}</p>
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Fuentes:</div>
                  <div className="flex flex-wrap gap-2">
                    {method?.sources?.map((source, idx) => (
                      <span key={idx} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Sources Tab */}
      {activeTab === 'sources' && (
        <div className="space-y-4">
          {dataSources?.map((source, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-foreground">{source?.organization}</h4>
                <span className={`text-xs px-2 py-1 rounded ${
                  source?.reliability === 'Muy Alta' ? 'bg-success/20 text-success' : 'bg-primary/20 text-primary'
                }`}>
                  {source?.reliability}
                </span>
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">
                {source?.description}
              </p>
              
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={12} />
                  <span>Actualizado: {new Date(source.lastUpdate)?.toLocaleDateString('es-ES')}</span>
                </div>
              </div>
            </div>
          ))}

          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Shield" size={20} color="var(--color-primary)" />
              <div>
                <h5 className="font-medium text-foreground text-sm mb-1">
                  Compromiso con la Precisión
                </h5>
                <p className="text-xs text-muted-foreground">
                  Actualizamos nuestros factores de cálculo trimestralmente basándonos en las últimas 
                  investigaciones científicas y estándares internacionales. Todos nuestros métodos 
                  están auditados por organizaciones independientes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Verification Tab */}
      {activeTab === 'verification' && (
        <div className="space-y-6">
          <div className="space-y-4">
            {verificationProcess?.map((step) => (
              <div key={step?.step} className="flex items-start space-x-4 p-4 bg-muted/30 rounded-lg">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">
                  {step?.step}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name={step?.icon} size={16} color="var(--color-primary)" />
                    <h4 className="font-medium text-foreground">{step?.title}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {step?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                <h5 className="font-medium text-success">Certificación ISO 14064</h5>
              </div>
              <p className="text-xs text-muted-foreground">
                Nuestros cálculos de huella de carbono están certificados bajo el estándar internacional ISO 14064.
              </p>
            </div>

            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Award" size={16} color="var(--color-primary)" />
                <h5 className="font-medium text-primary">Auditoría Independiente</h5>
              </div>
              <p className="text-xs text-muted-foreground">
                Auditados mensualmente por EcoMetrics, empresa certificada en medición de impacto ambiental.
              </p>
            </div>
          </div>

          <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
              <div>
                <h5 className="font-medium text-warning text-sm mb-1">
                  Margen de Error
                </h5>
                <p className="text-xs text-muted-foreground">
                  Nuestros cálculos tienen un margen de error del ±5%, considerado excelente en la industria. 
                  Los factores de conversión se actualizan regularmente para mantener la máxima precisión.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MethodologyExplainer;